import { useEffect, useState } from 'react'
import { CitatorAccordion } from './CitatorAccordion'
import { CitatorCaseListItem } from './CitatorCaseListItem'
import { CitatorCasesLoadingSkeleton } from './CitatorCasesLoadingSkeleton'
import { buildCaselawStandaloneViewerURL } from '@/standalone-reference-viewer/caselaw/utils/build-caselaw-viewer-url'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { trackRiaCitatorSectionExpanded, trackRiaCitatorCaseSelected, resolveCitatorCaseId, RiaSourceContext } from '@/analytics/utils/ria-source-analytics'
import { RiaCitatorSection } from '@/analytics/enums/ria.enums'
import type { CitationMetadata } from '@/citator/schema/citation-metadata-schema'
import type { CitingCase } from '@/citator/schema/case-review-schema'

export type CitatorCasesAccordionProps = {
  title: string
  /** Analytics section enum; RIA + legacy share underlying string identifiers. */
  section: RiaCitatorSection
  /** Case citations may include review fields; related cases use bare citation metadata. */
  data: Array<CitingCase | CitationMetadata> | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
  filterQuery: string
  analyticsContext?: RiaSourceContext | null
  /** When true, the accordion starts open (e.g. overruling-risk click-through). */
  defaultOpen?: boolean
}

/**
 * Default-collapsed accordion over a flat list (case citations, related cases). The badge
 * count tracks total fetched cases, not the filtered subset, per design.
 */
export function CitatorCasesAccordion({
  title,
  section,
  data,
  isLoading,
  isError,
  refetch,
  filterQuery,
  analyticsContext,
  defaultOpen = false,
}: CitatorCasesAccordionProps) {
  const { trackEvent } = useAnalytics()
  const filteredData = data?.filter((c) => c.name.toLowerCase().includes(filterQuery.toLowerCase())) ?? []
  const isFiltering = filterQuery.trim().length > 0
  const [isOpen, setIsOpen] = useState(defaultOpen)

  useEffect(() => {
    if (isFiltering) {
      setIsOpen(true)
    }
  }, [isFiltering])

  const handleAccordionOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open || !analyticsContext) return
    trackRiaCitatorSectionExpanded({
      trackEvent,
      context: analyticsContext,
      section,
      sectionCount: data?.length,
    })
  }

  return (
    <CitatorAccordion title={title} count={data?.length} open={isOpen} onOpenChange={handleAccordionOpenChange}>
      {isLoading && <CitatorCasesLoadingSkeleton />}

      {isError && (
        <div className="py-2">
          <div className="text-red-700 text-sm mb-2">Could not load {title.toLowerCase()}.</div>
          <button type="button" onClick={refetch} className="rounded-md bg-brand-500 px-3 py-1 text-sm font-semibold text-white hover:bg-brand-400">
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && data && data.length === 0 && <div className="py-2 text-sm text-brand-neutral-700">No {title.toLowerCase()} found.</div>}

      {!isLoading && !isError && data && data.length > 0 && filteredData.length === 0 && (
        <div className="py-2 text-sm text-brand-neutral-700">No matches for "{filterQuery}".</div>
      )}

      {!isLoading && !isError && filteredData.length > 0 && (
        <div className="flex flex-col gap-4">
          {filteredData.map((c, index) => {
            const number = index + 1
            const href =
              c.document_type === 'caselaw' && (c.parent_id || c.s3_path) ? buildCaselawStandaloneViewerURL(c, analyticsContext ?? undefined) : undefined
            const handleLinkClick = href
              ? () => {
                  const caseId = resolveCitatorCaseId({ parentId: c.parent_id, s3Path: c.s3_path })
                  if (!caseId || !analyticsContext) return
                  trackRiaCitatorCaseSelected({
                    trackEvent,
                    context: analyticsContext,
                    section,
                    caseId,
                    caseTitle: c.name,
                  })
                }
              : undefined

            return (
              <CitatorCaseListItem key={`${c.parent_id ?? c.s3_path ?? c.name}-${index}`} case={c} number={number} href={href} onLinkClick={handleLinkClick} />
            )
          })}
        </div>
      )}
    </CitatorAccordion>
  )
}
